import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Bell, CheckCircle2, XCircle, CalendarClock, Download, RefreshCw, Search } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS = {
  pending: ["En attente de réponse", "bg-amber-100 text-amber-800"],
  accepted: ["Acceptée — sans créneau", "bg-sky-100 text-sky-800"],
  confirmed: ["Confirmée", "bg-emerald-100 text-emerald-800"],
  declined: ["Déclinée", "bg-neutral-200 text-neutral-700"],
  cancelled: ["Annulée", "bg-rose-100 text-rose-700"],
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "UTC" }) + " UTC" : "—");
const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const SlotPicker = ({ row, grid, onPick, onClose }) => {
  const opts = grid.filter((g) => row.source !== "speed_meeting" || g.slot_id === row.slot_id);
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-neutral-50 p-2" data-testid={`slot-picker-${row.id}`}>
      <select defaultValue="" onChange={(e) => e.target.value && onPick(e.target.value)} className="rounded-lg border border-neutral-200 px-2 py-1 text-xs" data-testid={`slot-select-${row.id}`}>
        <option value="">Choisir un créneau…</option>
        {opts.map((g) => <option key={g.key} value={g.key} disabled={g.key === row.slot_key}>{g.slot_title} · {fmt(g.start)}</option>)}
      </select>
      <button onClick={onClose} className="text-xs text-neutral-500 hover:underline">Fermer</button>
    </div>
  );
};

/** Admin view of every meeting of an event (networking requests + speed-meeting bookings). */
export const EventMeetingsAdmin = ({ eventId }) => {
  const [data, setData] = useState({ rows: [], grid: [], slots: [] });
  const [f, setF] = useState({ status: "", q: "", slot: "", source: "" });
  const [picker, setPicker] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => api.get(`/admin/events/${eventId}/meetings`).then((r) => setData(r.data)).catch(() => toast.error("Chargement des RDV impossible")), [eventId]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => data.rows.filter((r) => {
    const q = f.q.toLowerCase();
    return (!f.status || r.status === f.status) && (!f.source || r.source === f.source) && (!f.slot || r.slot_id === f.slot)
      && (!q || [r.a_name, r.a_email, r.b_name, r.b_email].some((v) => (v || "").toLowerCase().includes(q)));
  }).sort((a, b) => (a.start || "9").localeCompare(b.start || "9")), [data.rows, f]);
  const counts = useMemo(() => data.rows.reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {}), [data.rows]);

  const act = async (row, action, body = {}) => {
    if (action === "cancel" && !window.confirm(`Annuler le RDV ${row.a_name} × ${row.b_name} ? Les deux participants seront prévenus.`)) return;
    setBusy(`${row.id}:${action}`);
    try {
      const { data: res } = await api.post(`/admin/events/${eventId}/meetings/${encodeURIComponent(row.id)}/${action}`, body);
      if (action === "remind") toast.success(`Relance envoyée (${res.sent})`);
      else if (res.status === "accepted") { toast.warning(res.detail); setPicker(row.id); }
      else toast.success(action === "cancel" ? "RDV annulé" : `RDV confirmé — ${fmt(res.slot_start)}`);
      if (res.status !== "accepted") setPicker(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
    finally { setBusy(null); }
  };

  const exportCsv = () => {
    const head = ["Type", "Statut", "Participant A", "E-mail A", "Participant B", "E-mail B", "Session", "Début", "Lieu", "Message"];
    const lines = rows.map((r) => [r.source === "networking" ? "Networking" : "Speed meeting", STATUS[r.status]?.[0] || r.status, r.a_name, r.a_email, r.b_name, r.b_email, r.slot_title, r.start, r.location, r.message].map(csvCell).join(";"));
    const blob = new Blob(["\ufeff" + [head.map(csvCell).join(";"), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `rdv-${eventId}.csv` });
    a.click();
  };

  const Btn = ({ row, action, icon: Icon, label, onClick, tone = "neutral" }) => (
    <button onClick={onClick || (() => act(row, action))} disabled={!!busy} data-testid={`meeting-${action}-${row.id}`}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold disabled:opacity-50 ${tone === "wine" ? "bg-wine-500 text-white hover:bg-wine-600" : tone === "rose" ? "border border-rose-200 text-rose-700 hover:bg-rose-50" : "border border-neutral-200 text-neutral-700 hover:bg-neutral-50"}`}>
      <Icon className="h-3.5 w-3.5" />{label}
    </button>
  );

  return (
    <div className="mt-6 space-y-4" data-testid="event-meetings-admin">
      <div className="flex flex-wrap items-center gap-2">
        {Object.entries(STATUS).map(([k, [label, cls]]) => (
          <button key={k} onClick={() => setF({ ...f, status: f.status === k ? "" : k })} className={`rounded-full px-3 py-1 text-xs font-semibold ${cls} ${f.status === k ? "ring-2 ring-navy" : ""}`} data-testid={`meetings-count-${k}`}>{label} · {counts[k] || 0}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative"><Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-neutral-400" />
          <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Participant ou e-mail" className="rounded-full border border-neutral-200 py-1.5 pl-8 pr-3 text-sm" data-testid="meetings-search" /></div>
        <select value={f.slot} onChange={(e) => setF({ ...f, slot: e.target.value })} className="rounded-full border border-neutral-200 px-3 py-1.5 text-sm" data-testid="meetings-slot-filter">
          <option value="">Toutes les sessions</option>{data.slots.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
        </select>
        <select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} className="rounded-full border border-neutral-200 px-3 py-1.5 text-sm" data-testid="meetings-source-filter">
          <option value="">Tous les types</option><option value="networking">Demandes networking</option><option value="speed_meeting">Créneaux speed meeting</option>
        </select>
        <button onClick={load} className="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-3 py-1.5 text-xs font-semibold" data-testid="meetings-refresh"><RefreshCw className="h-3.5 w-3.5" />Actualiser</button>
        <button onClick={exportCsv} className="ml-auto inline-flex items-center gap-1 rounded-full bg-navy px-3 py-1.5 text-xs font-bold text-white" data-testid="meetings-export-csv"><Download className="h-3.5 w-3.5" />Export CSV ({rows.length})</button>
      </div>
      <div className="overflow-hidden rounded-2xl border border-neutral-100 bg-white">
        {rows.length === 0 && <div className="p-8 text-center text-sm text-neutral-500" data-testid="meetings-empty">Aucun rendez-vous.</div>}
        {rows.map((r) => {
          const open = ["pending", "accepted", "confirmed"].includes(r.status);
          return (
            <div key={r.id} className="border-b border-neutral-100 p-4 last:border-0" data-testid={`meeting-row-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS[r.status]?.[1]}`}>{STATUS[r.status]?.[0]}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">{r.source === "networking" ? "Networking" : "Speed meeting"}</span>
                    {r.confirmed_by_admin && <span className="text-[10px] font-semibold text-emerald-700">validé par l'admin</span>}
                  </div>
                  <p className="mt-1 text-sm font-semibold text-navy">{r.a_name} <span className="text-neutral-400">×</span> {r.b_name}</p>
                  <p className="text-xs text-neutral-500">{r.a_email} · {r.b_email}</p>
                  <p className="mt-1 text-xs text-neutral-600">{r.start ? `${r.slot_title || "Speed meeting"} · ${fmt(r.start)}` : "Créneau à définir"}{r.location ? ` · ${r.location}` : ""}
                    {r.awaiting && <span className="ml-2 font-semibold text-amber-700">{r.awaiting === "target" ? `En attente de ${r.b_name}` : "Disponibilités à ouvrir (les deux)"}</span>}
                    {r.last_reminded_at && <span className="ml-2 text-neutral-400">relancé le {fmt(r.last_reminded_at)}</span>}</p>
                  {r.message && <p className="mt-1 text-xs italic text-neutral-500">« {r.message} »</p>}
                </div>
                {open && (
                  <div className="flex flex-wrap gap-1.5">
                    <Btn row={r} action="remind" icon={Bell} label="Relancer" />
                    {r.source === "networking" && r.status !== "confirmed" && <Btn row={r} action="confirm" icon={CheckCircle2} label="Valider" tone="wine" />}
                    <Btn row={r} action="reassign" icon={CalendarClock} label={r.status === "confirmed" ? "Changer de créneau" : "Choisir un créneau"} onClick={() => setPicker(picker === r.id ? null : r.id)} />
                    <Btn row={r} action="cancel" icon={XCircle} label="Annuler" tone="rose" />
                  </div>
                )}
              </div>
              {picker === r.id && <SlotPicker row={r} grid={data.grid} onClose={() => setPicker(null)} onPick={(key) => act(r, r.status === "confirmed" || r.source === "speed_meeting" ? "reassign" : "confirm", { slot_key: key })} />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EventMeetingsAdmin;
